import { Flex, Skeleton, TableColumnsType, TransferProps } from "antd";
import TableTransfer, { DataType, TableTransferProps } from "../../common/TableTransfer";
import { useCallback, useEffect, useRef, useState } from "react";
import { UserService } from "../../../services/user.api.client";
import { useCompany } from "../../../contexts/CompanyContext";
import { useConfig } from "../../../contexts/ConfigContext";
import { Calendar } from "../../../models/calendar/Calendar.model";
import UserConfigBoardEvent from "../../common/UserConfigBoardEvent.events";

type Props = {
  calendar: Calendar;
};

const columns: TableColumnsType<DataType> = [
  {
    dataIndex: 'label',
    title: 'Nombre',
  }
];

const ConfigurationSetUsers: React.FC<Props> = ({ calendar }) => {
  const { company } = useCompany();
  const { config } = useConfig();
  const [users, setUsers] = useState<DataType[]>([]);
  const [currentUser, setCurrentUser] = useState<DataType>();
  const [targetKeys, setTargetKeys] = useState<TransferProps['targetKeys']>([]);
  const [loadUser, setLoadUser] = useState<boolean>(true);
  const [configPermissions, setConfigPermissions] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Initialize: extract assigned users from calendar.users and load all company users
  useEffect(() => {
    // Extract assigned user keys from calendar.users (enriched objects or strings)
    const assignedKeys: string[] = (calendar.users || []).map((u: any) => {
      if (typeof u === 'object' && u !== null) return u.uid || u.id?.toString() || '';
      return String(u);
    }).filter(Boolean);

    setTargetKeys(assignedKeys);

    // Build initial dataSource from calendar.users (assigned users already known)
    const assignedDataItems: DataType[] = (calendar.users || [])
      .filter((u: any) => typeof u === 'object' && u !== null)
      .map((u: any) => ({
        key: u.uid || u.id?.toString() || '',
        label: u.name || 'Sin nombre',
        numericId: typeof u.id === 'number' ? u.id : undefined,
      }));

    // Fetch all company users, then merge
    UserService.getAllUsers(company.current.uid).then((response) => {
      const allUsers: DataType[] = (response || []).map((user: any) => ({
        label: user.name,
        key: user.uid || user.uidUser,
        numericId: typeof user.id === 'number' ? user.id : undefined,
      }));

      // Merge: keep all fetched users + any assigned that weren't in the fetch
      const mergedMap = new Map<string, DataType>();
      for (const u of allUsers) mergedMap.set(u.key, u);
      for (const u of assignedDataItems) {
        if (!mergedMap.has(u.key)) mergedMap.set(u.key, u);
      }

      setUsers(Array.from(mergedMap.values()));
      setLoadUser(false);
    }).catch(() => {
      // Fallback: at least show assigned users
      setUsers(assignedDataItems);
      setLoadUser(false);
    });

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [company.current.uid, calendar]);

  // Debounced semantic search via Algolia
  const handleSearch = useCallback((direction: string, value: string) => {
    if (direction !== 'left' || !value || value.length < 2) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(async () => {
      if (!company?.current?.uid || !config.key) return;
      try {
        const response = await UserService.searchBulkUsers(value, company.current.uid, config.key);
        if (response?.userList) {
          setUsers(prev => {
            const existingKeys = new Set(prev.map(u => u.key));
            const newItems: DataType[] = response.userList
              .filter((u: any) => !existingKeys.has(u.uid))
              .map((u: any) => ({
                key: u.uid,
                label: u.name,
                numericId: typeof u.id === 'number' ? u.id : undefined,
              }));
            return [...prev, ...newItems];
          });
        }
      } catch (error) {
        console.error('Error searching users:', error);
      }
    }, 800);
  }, [company?.current?.uid, config.key]);

  const onChange: TableTransferProps['onChange'] = (nextTargetKeys) => {
    setTargetKeys(nextTargetKeys);
  };

  const handlerCurrentUser = (key: string) => {
    setConfigPermissions(true);
    const userSelected = users.find((user) => user.key == key);
    setCurrentUser(userSelected);
  }

  if (loadUser) {
    return (
      <Flex align="start" gap="large" justify="space-between">
        <Skeleton.Node active={true} style={{ width: 300, height: 300 }} />
        <Skeleton.Node active={true} style={{ width: 300, height: 300 }} />
      </Flex>
    );
  } else if (configPermissions) {
    return (
      <section>
        {currentUser?.key && <UserConfigBoardEvent id={currentUser?.key} back={(exit) => setConfigPermissions(exit)} />}
      </section>
    );
  } else {
    return (
      <Flex align="start" gap="middle" vertical>
        <TableTransfer
          titles={['Usuarios Disponibles', 'Usuarios Asignados']}
          dataSource={users}
          targetKeys={targetKeys}
          showSearch
          showSelectAll={false}
          onChange={onChange}
          filterOption={(input, item) => item.label?.toLowerCase().includes(input.toLowerCase())}
          leftColumns={columns}
          rightColumns={columns}
          onSearch={handleSearch}
          calendar={calendar}
        />
      </Flex>
    );
  }
};

export default ConfigurationSetUsers;